import { useEffect, useRef } from 'react';

/**
 * SYM-6059. Ask a query again when the customer under it changes.
 *
 * ## Why this is needed at all
 *
 * Switching customer does not reload the app. `setActiveCustomer` writes the session to
 * localStorage and fires a `storage` event; NavigationContext hears it and moves one piece of
 * state (`NavigationContext.tsx:335-357`). Everything stays mounted.
 *
 * A query whose answer depends on the customer but whose VARIABLES do not mention one is therefore
 * never asked again: Apollo re-runs a query when its variables change, and `useQuery` from
 * `hooks/sympl-query` puts the customer in a HEADER, not in the variables. The header is rebuilt on
 * every render and then never sent, because no request goes out.
 *
 * `fetchPolicy: 'network-only'` does not cover this. It decides what happens when a query RUNS; it
 * cannot make one run.
 *
 * ## What went wrong without it
 *
 * Measured on QA on 26 Aug 2026: with customer 23138 open (seven ad templates) and then a switch to
 * 23135 (three), all seven stayed on screen. The server then refused to draw the four this customer
 * may not have -- correctly -- and each refusal became an error toast. The catalogue had simply
 * never been asked for again.
 *
 * ## Deliberately not "refetch everything"
 *
 * Point it only at queries whose answer really does change with the customer. A blanket refetch of
 * the whole store on every switch would be a much bigger hammer, and slower, than saying which
 * three answers actually go stale.
 *
 * @param customerId the active customer; `undefined` while none is chosen yet
 * @param refetch    the query's own refetch, from the same useQuery result
 */
export const useRefetchOnCustomerChange = (
  customerId: number | undefined,
  refetch: () => unknown
): void => {
  // The customer this hook has already seen. Starting from the FIRST value rather than from
  // undefined is the point: the query has just fetched for that customer on mount, and refetching
  // it immediately would double every page load for no new answer.
  const seen = useRef<number | undefined>(customerId);

  useEffect(() => {
    if (customerId === undefined) return;

    const previous = seen.current;
    seen.current = customerId;

    // Arriving at the first customer is not a switch. The query is skipped until there is one and
    // then fetches for it by itself, so refetching here would send every page load twice.
    if (previous === undefined) return;
    if (previous === customerId) return;

    refetch();
    // `refetch` is deliberately not a dependency. Apollo hands back a new function identity on
    // most renders, and depending on it would fire this effect on renders where the customer has
    // not moved -- which is the opposite of what it is for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);
};

export default useRefetchOnCustomerChange;
